/**
 * FileValidationService - User-Friendly File Validation Notifications
 *
 * This service displays friendly, approachable notifications for file validation
 * issues (wrong extension, format mismatch, missing columns, etc.).
 *
 * IMPORTANT: This is DIFFERENT from errorService which handles system/technical errors.
 *
 * Comparison:
 * - errorService: Red, alarming, technical, "something broke" - for system errors
 * - fileValidationService: Blue/amber, friendly, "let's fix this together" - for user file issues
 *
 * Design Philosophy:
 * - Users shouldn't feel like they "broke something" when their file has issues
 * - Clear, actionable guidance helps users fix problems quickly
 * - Technical details available but hidden by default (progressive disclosure)
 *
 * Persona Alignment:
 * - Debra: Simple language, encouraging tone, clear steps
 * - Doug: Technical details available on demand, exact validation rules
 * - Sarah: Debugging info expandable, file metadata for troubleshooting
 */

import React from 'react'
import { Modal, Button, Collapse, List, Typography, Space } from 'antd'
import {
  InfoCircleFilled,
  ExclamationCircleFilled,
  FileExcelOutlined,
  CodeOutlined,
} from '@ant-design/icons'

const { Text, Paragraph } = Typography

/**
 * A single validation issue found in the user's file
 */
export interface FileValidationIssue {
  /** Severity: error blocks progress, warning allows continue, info is FYI */
  type: 'error' | 'warning' | 'info'
  /** Internal code for logging/debugging */
  code: string
  /** User-friendly message explaining the issue */
  message: string
  /** Suggestion for how to fix the issue */
  suggestion?: string
  /** Link to help documentation */
  helpLink?: string
}

/**
 * Options for displaying file validation issues
 */
export interface FileValidationOptions {
  /** Name of the file that was validated */
  fileName: string
  /** List of validation issues found */
  issues: FileValidationIssue[]
  /** Technical details for debugging (hidden by default) */
  technicalDetails?: Record<string, any>
  /** Callback when user clicks "Try Another File" */
  onRetry?: () => void
  /** Callback when user clicks "Continue Anyway" (for warnings only) */
  onContinue?: () => void
  /** Whether to show a "Download Template" button */
  showTemplate?: boolean
  /** URL for template download */
  templateUrl?: string
}

/**
 * Content component for the file validation modal
 */
const FileValidationContent: React.FC<{
  icon: React.ReactNode
  title: string
  subtitle: string
  fileName: string
  issues: FileValidationIssue[]
  technicalDetails?: Record<string, any>
  showTemplate?: boolean
  templateUrl?: string
}> = ({ icon, title, subtitle, fileName, issues, technicalDetails, showTemplate: _showTemplate, templateUrl: _templateUrl }) => {
  const getIssueIcon = (type: FileValidationIssue['type']) => {
    switch (type) {
      case 'error':
        return <ExclamationCircleFilled style={{ color: 'var(--rp-temporal-orange)' }} />
      case 'warning':
        return <ExclamationCircleFilled style={{ color: 'var(--rp-temporal-orange)' }} />
      case 'info':
        return <InfoCircleFilled style={{ color: 'var(--rp-blue-primary)' }} />
      default:
        return <InfoCircleFilled style={{ color: 'var(--rp-blue-primary)' }} />
    }
  }

  return (
    <div>
      {/* Icon + Title Section */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          marginBottom: '20px',
        }}
      >
        {icon}
        <div>
          <h3
            style={{
              fontSize: '18px',
              fontWeight: 600,
              margin: 0,
              marginBottom: '8px',
              color: 'var(--rp-gray-900)',
            }}
          >
            {title}
          </h3>
          <Paragraph
            style={{
              fontSize: '14px',
              color: 'var(--rp-gray-600)',
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </Paragraph>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <FileExcelOutlined style={{ marginRight: '4px' }} />
            {fileName}
          </Text>
        </div>
      </div>

      {/* Validation Issues List */}
      <div
        style={{
          backgroundColor: 'var(--rp-surface-page)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          border: '1px solid var(--rp-temporal-blue-border)',
        }}
      >
        <List
          size="small"
          dataSource={issues}
          renderItem={(issue) => (
            <List.Item style={{ border: 'none', padding: '8px 0' }}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space>
                  {getIssueIcon(issue.type)}
                  <Text strong>{issue.message}</Text>
                </Space>
                {issue.suggestion && (
                  <Text type="secondary" style={{ marginLeft: '22px', display: 'block' }}>
                    {issue.suggestion}
                  </Text>
                )}
                {issue.helpLink && (
                  <a
                    href={issue.helpLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: '22px', fontSize: '12px' }}
                  >
                    Learn more
                  </a>
                )}
              </Space>
            </List.Item>
          )}
        />
      </div>

      {/* Technical Details (Progressive Disclosure) */}
      {technicalDetails && (
        <Collapse
          ghost
          expandIconPosition="start"
          items={[
            {
              key: 'technical',
              label: (
                <Space size={4}>
                  <CodeOutlined style={{ fontSize: '12px', color: 'var(--rp-gray-500)' }} />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Technical Details
                  </Text>
                </Space>
              ),
              children: (
                <div
                  style={{
                    backgroundColor: 'var(--rp-surface-tertiary)',
                    border: '1px solid var(--rp-border-subtle)',
                    borderRadius: '4px',
                    padding: '12px',
                    fontSize: '11px',
                    fontFamily: 'SFMono-Regular, Consolas, monospace',
                    color: 'var(--rp-gray-600)',
                    overflow: 'auto',
                    maxHeight: '150px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {technicalDetails.originalMessage || JSON.stringify(technicalDetails, null, 2)}
                </div>
              ),
            },
          ]}
          style={{ marginTop: '16px', marginBottom: '16px' }}
        />
      )}
    </div>
  )
}

/**
 * FileValidationService - Singleton service for displaying user-friendly file validation messages
 */
class FileValidationService {
  /**
   * Display a user-friendly modal for file validation issues
   *
   * @param options - Configuration for the validation notification
   */
  showValidationIssues(options: FileValidationOptions): void {
    const {
      fileName,
      issues,
      technicalDetails,
      onRetry,
      onContinue,
      showTemplate,
      templateUrl,
    } = options

    // Determine severity based on issues
    const hasErrors = issues.some((i) => i.type === 'error')
    const hasWarnings = issues.some((i) => i.type === 'warning')

    // Choose icon and title based on severity
    let icon: React.ReactNode
    let title: string
    let subtitle: string

    if (hasErrors) {
      icon = (
        <ExclamationCircleFilled style={{ fontSize: '48px', color: 'var(--rp-temporal-orange)', flexShrink: 0 }} />
      )
      title = issues.length === 1 ? "Let's Fix This" : 'A Few Things to Fix'
      subtitle =
        issues.length === 1
          ? 'We found an issue that needs your attention before we can process your file.'
          : `We found ${issues.length} issues that need your attention before we can process this file.`
    } else if (hasWarnings) {
      icon = <ExclamationCircleFilled style={{ fontSize: '48px', color: 'var(--rp-temporal-orange)', flexShrink: 0 }} />
      title = 'Almost There!'
      subtitle = "Your file looks good, but there are a few things you might want to check."
    } else {
      icon = <InfoCircleFilled style={{ fontSize: '48px', color: 'var(--rp-blue-primary)', flexShrink: 0 }} />
      title = "Let's Check Your File"
      subtitle = 'Here are some things to know about your file.'
    }

    // Modal instance reference for programmatic close
    let modalInstance: ReturnType<typeof Modal.info> | null = null

    // Handler that closes modal (afterClose will call onRetry)
    const handleRetryClick = () => {
      modalInstance?.destroy()
    }

    // Handler for "Continue Anyway" - closes modal and calls onContinue
    // Note: afterClose still calls onRetry, but onContinue takes precedence in the UI flow
    const handleContinueClick = () => {
      modalInstance?.destroy()
      onContinue?.()
    }

    // Build footer buttons
    const footer: React.ReactNode[] = []

    // "Continue Anyway" button for warnings only
    if (!hasErrors && onContinue) {
      footer.push(
        <Button key="continue" onClick={handleContinueClick}>
          Continue Anyway
        </Button>
      )
    }

    // "Try Another File" button
    footer.push(
      <Button key="retry" type="primary" onClick={handleRetryClick}>
        Try Another File
      </Button>
    )

    // Show the modal
    modalInstance = Modal.info({
      icon: null,
      title: null,
      content: (
        <FileValidationContent
          icon={icon}
          title={title}
          subtitle={subtitle}
          fileName={fileName}
          issues={issues}
          technicalDetails={technicalDetails}
          showTemplate={showTemplate}
          templateUrl={templateUrl}
        />
      ),
      okButtonProps: { style: { display: 'none' } }, // Hide default OK button
      footer: (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
          {footer}
        </div>
      ),
      centered: true,
      width: 520,
      closable: true,
      maskClosable: true,
      keyboard: true,
      // Always call onRetry when modal closes (any method: X, click outside, Escape, or button)
      // This resets the upload state to 'idle' so user can immediately try again
      afterClose: onRetry,
      className: 'file-validation-modal',
      styles: {
        content: {
          borderRadius: '12px',
          padding: '24px',
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.35)',
        },
      },
    })

    // Log for debugging (less alarming than errorService)
    console.info('FileValidationService:', {
      fileName,
      issues,
      technicalDetails,
      timestamp: new Date().toISOString(),
    })
  }

  /**
   * Convenience method for showing a single file extension error
   */
  showInvalidExtension(
    fileName: string,
    allowedExtensions: string[],
    onRetry?: () => void
  ): void {
    const extension = fileName.split('.').pop()?.toLowerCase() || 'unknown'
    const allowedList = allowedExtensions.map((ext) => `.${ext}`).join(', ')

    this.showValidationIssues({
      fileName,
      issues: [
        {
          type: 'error',
          code: 'INVALID_EXTENSION',
          message: `We need a different file type`,
          suggestion: `Your file is .${extension}, but we need one of: ${allowedList}. Try saving your file in one of these formats.`,
        },
      ],
      technicalDetails: {
        detected: extension,
        allowed: allowedExtensions,
      },
      onRetry,
      showTemplate: true,
    })
  }

  /**
   * Convenience method for showing a file format mismatch error
   * (e.g., file extension says .xlsx but content is CSV)
   */
  showFormatMismatch(
    fileName: string,
    expectedFormat: string,
    detectedFormat: string,
    onRetry?: () => void
  ): void {
    this.showValidationIssues({
      fileName,
      issues: [
        {
          type: 'error',
          code: 'FORMAT_MISMATCH',
          message: 'File format mismatch',
          suggestion: `This file is labeled as ${expectedFormat}, but the contents appear to be ${detectedFormat}. This usually happens when a file is renamed without being properly converted. Try opening the file in Excel and saving it as a proper ${expectedFormat} file.`,
        },
      ],
      technicalDetails: {
        expectedFormat,
        detectedFormat,
        fileName,
      },
      onRetry,
    })
  }

  /**
   * Convenience method for showing missing required columns
   */
  showMissingColumns(
    fileName: string,
    missingColumns: string[],
    onRetry?: () => void
  ): void {
    this.showValidationIssues({
      fileName,
      issues: [
        {
          type: 'error',
          code: 'MISSING_COLUMNS',
          message: 'Missing required columns',
          suggestion: `Your file needs these columns: ${missingColumns.join(', ')}. Add them to your file and try again.`,
        },
      ],
      technicalDetails: {
        missingColumns,
      },
      onRetry,
      showTemplate: true,
    })
  }

  /**
   * Convenience method for showing a generic processing error
   * that's still user-friendly (not a system error)
   */
  showProcessingIssue(
    fileName: string,
    message: string,
    suggestion?: string,
    onRetry?: () => void
  ): void {
    // Strip technical prefixes from backend messages to show only user-friendly content
    // Backend messages come as: "Error processing vendor file: FORMAT_MISMATCH: <friendly message>"
    // We want to show only the friendly message part, but preserve original for technical details
    const originalMessage = message
    let friendlyMessage = message

    // Remove common technical prefixes
    const technicalPrefixes = [
      /^Error processing (?:vendor|catalog) file:\s*/i,
      /^FORMAT_MISMATCH:\s*/i,
      /^INVALID_FORMAT:\s*/i,
      /^MISSING_COLUMNS:\s*/i,
      /^EMPTY_FILE:\s*/i,
      /^CORRUPTED_FILE:\s*/i,
      /^CSV_EMPTY:\s*/i,
      /^CSV_TOO_FEW_COLUMNS:\s*/i,
      /^CSV_NO_HEADER_ROW:\s*/i,
      /^CSV_DUPLICATE_COLUMNS:\s*/i,
    ]

    for (const prefix of technicalPrefixes) {
      friendlyMessage = friendlyMessage.replace(prefix, '')
    }

    // Only include technical details if we actually stripped something
    // (i.e., original message differs from friendly message)
    const technicalDetails = originalMessage !== friendlyMessage
      ? { originalMessage }
      : undefined

    this.showValidationIssues({
      fileName,
      issues: [
        {
          type: 'error',
          code: 'PROCESSING_ISSUE',
          message: friendlyMessage,
          suggestion,
        },
      ],
      technicalDetails,
      onRetry,
    })
  }
}

// Export singleton instance
export const fileValidationService = new FileValidationService()
export default fileValidationService
